import React, { useEffect, useState, useMemo } from "react";
import styles from "./HotelCalibrationComponent.module.css";
import LayoutWithLog from "../LayoutWithLog";
import MultipleSelectDropDown, {
  GroupedDropDownDisplayValue,
} from "../../../Common/DropDown/MultipleSelectDropDown";
import CheckBox from "../../../Buttons/CheckBox";
import SwitchButton from "../../../Buttons/SwitchButton";
import MtiButton from "../../../Buttons/MtiButton";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { ESuiteMode, EPsrState } from "../../../../Model/ModuleState";
import CommonDropDown from "../../../Common/DropDown/CommonDropDown";
import { CalibratedHotel } from "../../../../Model/HotelCalibrationResult";
import Modal from "../../../Common/Modal";
import ModalNotificationView from "../../../Common/ModalNotification";
import { ENotificationType } from "../../../../Model/ENotificationType";
import CalibrationApi from "../../../../api/CalibrationApi";
import { getStorageConfig } from "../../../../redux/reducers/StorageConfigReducer";
import {
  getModulesByType,
  getModules,
} from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";
import { StorageConfig } from "../../../../Model/StorageConfig";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import usePersistState from "../../../../Utility/usePersistState";
import Table from "./Table";
import { Column, SortingRule } from "react-table";
import HotelCalibrationResult from "../../../../Model/HotelCalibrationResult";
import { sendNotification } from "../../../../redux/actions/NotificationAction";
import { useDispatch } from "react-redux";
import useCalibrationLog from "../../../Hooks/useCalibrationLog";
import { calibrationSignalRMap } from "../SettingsStorageRi1000";

const PERSIST_STATE_NAME_PREFIX = "HotelCalibrationComponent";

enum EComponentState {
  Initial,
  Processing,
  Finished,
  Faulted,
}

interface Prop {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  setImportHandler: (onImport: {
    execute: (fileToImport: any) => void;
    disabled: boolean;
  }) => void;
  setExportHandler: (onExport: {
    execute: () => void;
    disabled: boolean;
  }) => void;
  setIsCalibrationUpdated: (isUpdated: boolean) => void;
  setResetHandler: (onReset: {
    execute: () => void;
    disabled: boolean;
  }) => void;
  setRunningTabKey: React.Dispatch<React.SetStateAction<string>>;
}

interface ColumnProp {
  header: any;
  cell: any;
}

function createColumn(
  accessor: string,
  { header, cell }: ColumnProp,
  getProps?: any
): Column {
  const column = {
    accessor: accessor,
    Header: header,
    Cell: cell,
    getProps: getProps ? getProps : () => {},
  };

  return column;
}

function roundToTwoDecimal(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

const checkSubset = (parentArray: any[], subsetArray: any[]) => {
  return subsetArray.every((el) => {
    return parentArray.includes(el);
  });
};

export default function HotelCalibrationComponent({
  setOnApply,
  setOnCancel,
  setImportHandler,
  setExportHandler,
  setIsCalibrationUpdated,
  setResetHandler,
  setRunningTabKey,
}: Prop) {
  ///// INITS /////
  const dispatch = useDispatch();
  const storageModule = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.CHAMBER][0]
  );

  const storageConfig = useTypedSelector((state) =>
    getStorageConfig(state, storageModule.identifier)
  );

  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Error;

  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrIdentifier = psrModules?.[0]?.identifier;
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier).psr
  );

  // GET LOG LOGIC
  const [logs] = useCalibrationLog(calibrationSignalRMap.hotel);

  ///// STATE OBJECTS /////

  const [selectedHotels, setSelectedHotels] = useState([] as number[]);

  const [tRef, setTRef] = useState(0);
  const [eRef, setERef] = useState(0);
  const [zRef, setZRef] = useState(0);

  const [allowRunCalibration, setAllowRunCalibration] = useState(true);
  const [allowStopCalibration, setAllowStopCalibration] = useState(false);

  const [allowSelectingHotel, setAllowSelectingHotel] = useState(false);

  const [tableData, setTableData] = useState({} as HotelCalibrationResult);
  const [summaryTabledata, setSummaryTableData] = useState(
    {} as HotelCalibrationResult
  );

  const [allowThetaOnly, setAllowThetaOnly] = useState(false);
  const [allowExtentsOnly, setallowExtentsOnly] = useState(false);
  const [allowRepeat, setAllowRepeat] = useState(false);

  const [shouldShowCompleteData, setShouldShowCompleteData] = useState(false);
  const [showResetWarning, setShowResetWarning] = useState(false);

  const [referenceHotelSelection, setReferenceHotelSelection] = useState(
    [] as number[]
  );
  const [selectedReferenceHotel, setSelectedReferenceHotel] = useState(
    referenceHotelSelection.length > 0 ? referenceHotelSelection[0] : 0
  );
  const [referenceHotelSelectionDisabled, setReferenceHotelSelectionDisabled] =
    useState(true);

  const [isThetaOnly, setIsThetaOnly] = useState(false);
  const [isExtentsOnly, setIsExtentsOnly] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);

  const [processState, setProcessState] = usePersistState(
    EComponentState.Initial,
    PERSIST_STATE_NAME_PREFIX + "ProcessState"
  );

  ///// END STATE OBJECTS /////

  ///// SIDE EFFECTS /////

  //----- allowSelectingHotel
  useEffect(() => {
    if (processState === EComponentState.Processing) {
      setAllowSelectingHotel(false);
      return;
    }

    setAllowSelectingHotel(true);
  }, [processState]);

  //----- allowRunAutoCalibration
  useEffect(() => {
    if (selectedHotels.length < 1) {
      setAllowRunCalibration(false);
      return;
    }

    if (processState === EComponentState.Processing) {
      setAllowRunCalibration(false);
      return;
    }

    setAllowRunCalibration(true);
  }, [selectedHotels, processState]);

  //----- allowStopAutoCalibration
  useEffect(() => {
    if (processState === EComponentState.Processing) {
      setAllowStopCalibration(true);
      return;
    }

    setAllowStopCalibration(false);
  }, [processState]);

  //----- allowThetaOnly & allowExtentsOnly
  useEffect(() => {
    if (tableData.hotels?.length < 1) {
      setAllowThetaOnly(false);
      setallowExtentsOnly(false);
      return;
    }

    if (
      !checkSubset(
        tableData.hotels?.map((t) => t.hotel) ?? [],
        selectedHotels
      ) ||
      selectedHotels.length < 1
    ) {
      setAllowThetaOnly(false);
      setallowExtentsOnly(false);
      return;
    }

    setAllowThetaOnly(true);
    setallowExtentsOnly(true);
  }, [selectedHotels, processState, tableData]);

  //----- allowRepeat
  useEffect(() => {
    if (processState === EComponentState.Processing) {
      setAllowRepeat(false);
      return;
    }

    setAllowRepeat(true);
  }, [processState]);

  //----- referenceHotelSelection
  useEffect(() => {
    const hotels =
      summaryTabledata.hotels?.length > 0
        ? summaryTabledata.hotels.map((s) => s.hotel).sort((d) => d)
        : [];
    setReferenceHotelSelection(hotels.sort((n1, n2) => n1 - n2));
    setSelectedReferenceHotel(tableData.referenceHotel ?? 0);
    setTRef(tableData.referenceHotelPosition?.t ?? 0);
    setERef(tableData.referenceHotelPosition?.e ?? 0);
    setZRef(tableData.referenceHotelPosition?.z ?? 0);
  }, [summaryTabledata]);

  //----- isThetaOnly
  useEffect(() => {
    if (!allowThetaOnly) setIsThetaOnly(allowThetaOnly);
  }, [allowThetaOnly]);

  //----- isExtentsOnly
  useEffect(() => {
    if (!allowExtentsOnly) setIsExtentsOnly(allowExtentsOnly);
  }, [allowThetaOnly]);

  //----- tableData
  useEffect(() => {
    if (processState === EComponentState.Finished) {
      setRunningTabKey("");
      CalibrationApi.getHotelCalibrationResult().then((result) => {
        setTableData(result);
      });
    }
  }, [processState]);

  useEffect(() => {
    if (!tableData || tableData.hotels?.length < 1) {
      setSummaryTableData({} as HotelCalibrationResult);
      return;
    }

    setSummaryTableData(tableData);
  }, [tableData]);

  //----- isHotelCalibrationUpdated
  useEffect(() => {
    if (tableData.hotels?.length < 1) {
      setIsHotelCalibrationUpdated(false);
      return;
    }

    if (processState === EComponentState.Processing) {
      setIsHotelCalibrationUpdated(false);
      return;
    }

    if (processState === EComponentState.Initial) {
      setIsHotelCalibrationUpdated(false);
      return;
    }

    setIsHotelCalibrationUpdated(true);
  }, [tableData, processState]);

  //----- referenceHotelDisabled
  useEffect(() => {
    if (processState == EComponentState.Processing) {
      setReferenceHotelSelectionDisabled(true);
      return;
    }

    if (referenceHotelSelection.length > 0) {
      setReferenceHotelSelectionDisabled(false);
      return;
    }

    setReferenceHotelSelectionDisabled(true);
  }, [referenceHotelSelection, processState]);

  //----- processState
  useEffect(() => {
    if (suiteMode == ESuiteMode.AutoCalibrating) {
      setProcessState(EComponentState.Processing);
      return;
    }

    if (suiteMode === ESuiteMode.Normal) {
      if (processState === EComponentState.Processing) {
        if (psrState?.state === EPsrState.Available) {
          setProcessState(EComponentState.Finished);
          return;
        }

        if (psrState?.state === EPsrState.Fail) {
          setProcessState(EComponentState.Initial);
          return;
        }
      }
    }

    if (suiteMode == ESuiteMode.ArmCrashed || suiteMode == ESuiteMode.Error) {
      setProcessState(EComponentState.Initial);
      return;
    }
  }, [suiteMode, suiteState]);

  useEffect(() => {
    CalibrationApi.getHotelCalibrationResult().then((result) =>
      setTableData(result)
    );
  }, []);

  useEffect(() => {
    setResetHandler({
      execute: () => {
        CalibrationApi.getHotelCalibrationResult().then((result) => {
          setTableData(result);
          setProcessState(EComponentState.Initial);
        });
      },
      disabled: false,
    });
  }, []);

  ///// END SIDE EFFECTS /////

  ///// INNER FUNCTIONS /////

  const importFileInputHandler = (fileToImport: any) => {
    let data = new FormData();
    data.append("file", fileToImport);
    CalibrationApi.importHotelCalibrationData(data).then(() => {
      CalibrationApi.getHotelCalibrationResult().then((result) => {
        setTableData(result);
        setIsHotelCalibrationUpdated(true);
      });
    });
  };

  const exportFileHandler = () => {
    CalibrationApi.exportHotelCalibrationData("HotelCalibrationData.csv");
  };

  useEffect(() => {
    setImportHandler({
      execute: importFileInputHandler,
      disabled: false,
    });

    setExportHandler({
      execute: exportFileHandler,
      disabled: false,
    });
  }, []);

  const getHotelSelections = (storageConfig: StorageConfig) => {
    const hotelSelections = {
      title: "Hotel",
      displayValues: [],
    } as GroupedDropDownDisplayValue<number>;

    for (let i = 1; i <= storageConfig.hotels.length; i++) {
      hotelSelections.displayValues.push({
        display: i.toString(),
        value: i,
        disabled: false,
      });
    }

    return [hotelSelections];
  };

  const setIsHotelCalibrationUpdated = (isUpdated: boolean) => {
    setOnApply({
      execute: () => {
        CalibrationApi.saveHotelCalibrationResult()
          .then(() => {
            dispatch(
              sendNotification(
                ENotificationType.Success,
                "The changes have been saved successfully"
              )
            );
            setIsHotelCalibrationUpdated(false);
            setProcessState(EComponentState.Initial);
          })
          .catch(() => {
            dispatch(
              sendNotification(
                ENotificationType.Error,
                "Saving changes encountered an error"
              )
            );
          });
      },
      disabled: !isUpdated,
    });

    setOnCancel({
      execute: () => {
        revertCalibrationResult();
        setIsHotelCalibrationUpdated(false);
        setProcessState(EComponentState.Initial);
      },
      disabled: !isUpdated,
    });

    setIsCalibrationUpdated(isUpdated);
  };

  const resetCalibrationResult = () => {
    CalibrationApi.resetHotelCalibrationResult().then(() => {
      CalibrationApi.getHotelCalibrationResult().then((result) => {
        setTableData(result);
        setProcessState(EComponentState.Initial);
      });
    });
  };

  const revertCalibrationResult = () => {
    CalibrationApi.revertHotelCalibrationResult().then(() => {
      CalibrationApi.getHotelCalibrationResult().then((result) => {
        setTableData(result);
        setProcessState(EComponentState.Initial);
      });
    });
  };

  const startCalibration = () => {
    setRunningTabKey(calibrationSignalRMap.hotel);
    CalibrationApi.runHotelCalibration({
      thetaOnly: isThetaOnly,
      extendsOnly: isExtentsOnly,
      hotels: selectedHotels,
      repeat: isRepeat,
    });
  };

  const stopCalibration = () => {
    CalibrationApi.stopHotelCalibration();
  };

  const onReferenceHotelChange = (value: number | "-") => {
    if (value === "-") return;

    setSelectedReferenceHotel(value);
    // setIsCalibrationUpdated(true);
    CalibrationApi.setHotelCalibrationHotelReference(value).then(() =>
      CalibrationApi.getHotelCalibrationResult().then((result) => {
        setTableData(result);
      })
    );
    setProcessState(EComponentState.Finished);
    setIsHotelCalibrationUpdated(true);
  };
  ///// END INNER FUNCTIONS /////

  ///// TABLE /////

  const resultTableColumns = useMemo(
    () => [
      createColumn("hotel", {
        header: () => <span>#</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("column", {
        header: () => <span>Col</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("row", {
        header: () => <span>Row</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsTopLeft", {
        header: () => <span>Extents TL (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsTopRight", {
        header: () => <span>Extents TR (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsBackLeft", {
        header: () => <span>Extents BL (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsBottomRight", {
        header: () => <span>Extents BR (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsTopLeftOffset", {
        header: () => <span>Extents TL Offs (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "extentsTopRightOffset",
        {
          header: () => <span>Extents TR Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesExtendsTopDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("extentsBottomLeftOffset", {
        header: () => <span>Extents BL Offs (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "extentsBottomRightOffset",
        {
          header: () => <span>Extents BR Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesExtendsBottomDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("thetaTopLeft", {
        header: () => <span>Theta TL (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaTopRight", {
        header: () => <span>Theta TR (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaTopAvg", {
        header: () => <span>Theta T Avg (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "thetaTopOffset",
        {
          header: () => <span>Theta T Offs (deg)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesThetaTopDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("thetaMiddleLeft", {
        header: () => <span>Theta ML (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaMiddleRight", {
        header: () => <span>Theta MR (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaMiddleAvg", {
        header: () => <span>Theta M Avg (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "thetaMiddleOffset",
        {
          header: () => <span>Theta M Offs (deg)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesThetaMiddleDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn(
        "thetaMiddleWidthOffset",
        {
          header: () => <span>Theta MW Offs (deg)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesThetaMiddleWidthDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("thetaBottomLeft", {
        header: () => <span>Theta BL (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaBottomRight", {
        header: () => <span>Theta BR (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaBottomAvg", {
        header: () => <span>Theta B Avg (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "thetaBottomOffset",
        {
          header: () => <span>Theta B Offs (deg)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesThetaBottomDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("bowing", {
        header: () => <span>Bowing (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("zTop", {
        header: () => <span>Z Top (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("zBottom", {
        header: () => <span>Z Bottom (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "zTopOffset",
        {
          header: () => <span>Z Top Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesZTopDeltaExceedLimit) return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn(
        "zBottomOffset",
        {
          header: () => <span>Z Bottom Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesHotelHeightDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
    ],
    []
  );

  const resutlSummaryTableColumns = useMemo(
    () => [
      createColumn("hotel", {
        header: () => <span>#</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("column", {
        header: () => <span>Col</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("row", {
        header: () => <span>Row</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsTopLeft", {
        header: () => <span>Extents TL (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsTopRight", {
        header: () => <span>Extents TR (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsBackLeft", {
        header: () => <span>Extents BL (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("extentsBottomRight", {
        header: () => <span>Extents BR (mm)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "extentsTopLeftOffset",
        {
          header: () => <span>Extents TL Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn(
        "extentsTopRightOffset",
        {
          header: () => <span>Extents TR Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn(
        "extentsBottomLeftOffset",
        {
          header: () => <span>Extents BL Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn(
        "extentsBottomRightOffset",
        {
          header: () => <span>Extents BR Offs (mm)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          if (row.doesExtendsBottomDeltaExceedLimit)
            return { background: "#FF9999" };

          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("thetaTopAvg", {
        header: () => <span>Theta T Avg (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaMiddleAvg", {
        header: () => <span>Theta M Avg (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn("thetaBottomAvg", {
        header: () => <span>Theta B Avg (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
      createColumn(
        "thetaBottomOffset",
        {
          header: () => <span>Theta B Offs (deg)</span>,
          cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
        },
        (cell: any, row: any) => {
          return Math.abs(cell.value) > 0 ? { background: "#FFEDAD" } : {};
        }
      ),
      createColumn("bowing", {
        header: () => <span>Bowing (deg)</span>,
        cell: (props: any) => <span>{roundToTwoDecimal(props.value)}</span>,
      }),
    ],
    []
  );

  const defaultSort: SortingRule<CalibratedHotel> = {
    id: "hotel",
    desc: false,
  };

  return (
    <div className={styles["outer-border"]}>
      <LayoutWithLog logMessages={logs ?? []}>
        <LayoutWithLog.Content>
          <div className={styles["calibration-view"]}>
            <div className={styles["calibration-settings"]}>
              <div className={styles["target-selection"]}>
                <div className={styles["selector"]}>
                  <div className={styles["label-std"]}>Hotel Selection</div>
                  <MultipleSelectDropDown
                    id="hotel-selection"
                    values={selectedHotels}
                    onChange={(selectedHotels) => {
                      setSelectedHotels(selectedHotels);
                    }}
                    dropDownDisplayValues={getHotelSelections(storageConfig)}
                    style={{ width: "250px" }}
                    disabled={!allowSelectingHotel}
                  />
                </div>
                <div className={styles["options"]}>
                  <CheckBox
                    colorVariant={"alternate"}
                    name="Theta Only"
                    id="theta-only-checkbox"
                    checked={isThetaOnly}
                    onChange={(id, value) => {
                      setIsThetaOnly(value);
                    }}
                    label="Theta Only"
                    style={{
                      fontFamily: "Roboto",
                      fontSize: "16px",
                    }}
                    size="small"
                    disabled={!allowThetaOnly}
                  />

                  <CheckBox
                    colorVariant={"alternate"}
                    name="Extents Only"
                    id="extents-only-checkbox"
                    checked={isExtentsOnly}
                    onChange={(id, value) => {
                      setIsExtentsOnly(value);
                    }}
                    label="Extents Only"
                    style={{
                      fontFamily: "Roboto",
                      fontSize: "16px",
                    }}
                    size="small"
                    disabled={!allowExtentsOnly}
                  />

                  <div className={styles["repeat-switch"]}>
                    <div
                      className={
                        allowRepeat
                          ? styles["label-std"]
                          : styles["label-std-disabled"]
                      }
                    >
                      Repeat
                    </div>
                    <SwitchButton
                      isOn={isRepeat}
                      onToggle={(value) => {
                        setIsRepeat(value);
                      }}
                      style={{
                        width: "32px",
                        fontFamily: "Roboto",
                        fontSize: "16px",
                      }}
                      disabled={!allowRepeat}
                    />
                  </div>
                </div>
              </div>
              <div className={styles["reference-settings"]}>
                <div
                  className={styles["label-std"]}
                  style={{ fontWeight: 500 }}
                >
                  Extents
                </div>
                <div className={styles["reference-selector"]}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      columnGap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <div className={styles["label-std"]}>Reference Hotel</div>
                    <CommonDropDown
                      id="hotel-selector"
                      value={selectedReferenceHotel}
                      dropDownDisplayValues={referenceHotelSelection.map(
                        (r) => {
                          return {
                            display: r.toString(),
                            disabled: false,
                            value: r,
                          };
                        }
                      )}
                      onChange={onReferenceHotelChange}
                      style={{
                        width: "112px",
                      }}
                      disabled={referenceHotelSelectionDisabled}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      columnGap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <div className={styles["label-std"]}>
                      T:{" "}
                      <span style={{ marginLeft: "16px", fontWeight: 500 }}>
                        {roundToTwoDecimal(tRef)}
                      </span>
                    </div>
                    <div className={styles["label-std"]}>
                      E:{" "}
                      <span style={{ marginLeft: "16px", fontWeight: 500 }}>
                        {roundToTwoDecimal(eRef)}
                      </span>
                    </div>
                    <div className={styles["label-std"]}>
                      Z:{" "}
                      <span style={{ marginLeft: "16px", fontWeight: 500 }}>
                        {roundToTwoDecimal(zRef)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles["calibration-control"]}>
              <div className={styles["label-std"]} style={{ width: "232px" }}>
                Calibrating Hotel
              </div>
              <div className={styles["button-group"]}>
                <MtiButton
                  variant="secondary"
                  colorVariant="black"
                  onClick={stopCalibration}
                  disabled={!allowStopCalibration}
                >
                  STOP
                </MtiButton>
                <MtiButton
                  variant="primary"
                  colorVariant="black"
                  onClick={startCalibration}
                  disabled={!(allowRunCalibration && selectedHotels.length > 0)}
                >
                  RUN
                </MtiButton>
              </div>
            </div>

            <div className={styles["results-view"]}>
              <div
                style={{
                  display: "flex",
                  boxSizing: "border-box",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingTop: "8px",
                  paddingBottom: "0px",
                }}
              >
                <div
                  className={styles["label-std"]}
                  style={{
                    fontSize: "24px",
                    marginRight: "auto",
                    marginBottom: "8px",
                  }}
                >
                  Result
                </div>
                <div
                  className={styles["button-group"]}
                  style={{
                    marginLeft: "auto",
                    columnGap: "8px",
                    height: "32px",
                    alignItems: "flex-end",
                  }}
                >
                  <button
                    className={styles["btn-style-red"]}
                    onClick={() => setShowResetWarning(true)}
                    disabled={
                      tableData.hotels?.length < 1 ||
                      processState === EComponentState.Processing ||
                      !tableData.hotels
                    }
                  >
                    RESET
                  </button>
                  <button
                    className={styles["btn-style-black"]}
                    onClick={() => setShouldShowCompleteData(true)}
                    disabled={
                      tableData.hotels?.length < 1 ||
                      processState === EComponentState.Processing ||
                      !tableData.hotels
                    }
                  >
                    SHOW ALL
                  </button>
                </div>
              </div>

              <Table
                columns={resutlSummaryTableColumns}
                data={summaryTabledata?.hotels ?? []}
                linePerPage={8}
                sortBy={[defaultSort]}
              />
            </div>
          </div>
        </LayoutWithLog.Content>
      </LayoutWithLog>
      <Modal
        show={shouldShowCompleteData}
        relativeToParent={true}
        onHide={() => setShouldShowCompleteData(false)}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          width: "fit-content",
          marginRight: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Modal.Title
            title="Hotel Calibration Result"
            style={{
              marginRight: "auto",
              color: "#474747",
            }}
          />
          <Modal.HideButton
            onClick={() => setShouldShowCompleteData(false)}
            style={{
              color: "#414141",
            }}
          />
        </div>
        <Table
          columns={resultTableColumns}
          data={tableData?.hotels ?? []}
          linePerPage={12}
          sortBy={[defaultSort]}
        />
      </Modal>
      <ModalNotificationView
        headerText="Clear Calibration Result"
        bodyTitleText="Are you sure to reset calibration result?"
        bodySubTitleText="Changes will make Hotel calibration result table empty"
        type={ENotificationType.Warning}
        show={showResetWarning}
        onHide={() => {}}
        onClickOk={() => {
          resetCalibrationResult();
          setShowResetWarning(false);
        }}
        onClickCancel={() => {
          setShowResetWarning(false);
        }}
        okLabel="OK"
        cancelLabel="CANCEL"
      />
    </div>
  );
}
